import React from 'react';
import BlogLayout from '../../components/BlogLayout';

// ─── INFOGRAPHIC 1: What 4 Hours Produced ─────────────────────────────────────
const FourHourTimeline = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: What 4 Hours of AI-Assisted Integration Actually Produced
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="space-y-4">
                {[
                    { time: 'Hour 1', icon: '🔑', title: 'API Authentication + Endpoint Audit', detail: '18 endpoints identified, authenticated, and tested. Every endpoint documented with live response data — what works, what lies, what is locked.' },
                    { time: 'Hour 2', icon: '🗄️', title: 'Database Design + Schema', detail: '13-table SQLite database designed and created. Every field from every working API response mapped to a local column. No API call needed for reports.' },
                    { time: 'Hour 3', icon: '⚙️', title: 'Scripts: Daily Sync + Weather Engine', detail: 'Automated ProLine-to-SQLite sync script. Rain day decision engine with geocoding, Open-Meteo forecasts, and a configurable risk rubric.' },
                    { time: 'Hour 4', icon: '📚', title: 'Knowledge Base + Skill Definition', detail: '7 workflow recipes documented. Full API reference written. Field gotchas captured. OpenClaw skill SKILL.md created. Every critical ID and constraint noted.' },
                ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5">
                        <div className="flex-shrink-0 w-24 text-right">
                            <span className="text-xs font-data text-[#7B61FF] uppercase tracking-wider">{item.time}</span>
                        </div>
                        <div className="w-px bg-[#7B61FF]/30 self-stretch mx-2" />
                        <div className="text-xl flex-shrink-0">{item.icon}</div>
                        <div>
                            <div className="font-heading font-bold text-[#F0EFF4] text-sm mb-1">{item.title}</div>
                            <div className="text-xs text-[#F0EFF4]/50 leading-relaxed">{item.detail}</div>
                        </div>
                    </div>
                ))}
            </div>
            <p className="text-center text-xs text-[#F0EFF4]/30 mt-6 italic">
                Built by an AI agent (Lucy) and a human (Adam Sand) working in real time, in a live ProLine account.
            </p>
        </div>
    </div>
);

// ─── INFOGRAPHIC 2: API Status Board ──────────────────────────────────────────
const APIStatusBoard = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: ProLine API — What the Integration Actually Found
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8 space-y-6">
            {/* Working */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-xs font-data uppercase tracking-wider text-green-400">7 Endpoints — Verified Working</span>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                    {[
                        ['find/project', 'Lookup project by ID'],
                        ['edit/project', 'Create or update a project'],
                        ['find/contact', 'Lookup contact by ID'],
                        ['find/event', 'Get appointments for a project'],
                        ['events/edit', 'Book or reschedule appointments'],
                        ['events/cancel', 'Cancel an appointment'],
                        ['find/team_member', 'Lookup team member by email'],
                    ].map(([ep, desc], i) => (
                        <div key={i} className="flex gap-3 items-center p-3 bg-green-500/5 border border-green-500/15 rounded-lg">
                            <code className="text-xs font-data text-green-400">{ep}</code>
                            <span className="text-xs text-[#F0EFF4]/40">{desc}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Broken */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-yellow-400" />
                    <span className="text-xs font-data uppercase tracking-wider text-yellow-400">4 Endpoints — Accepted But Non-Functional</span>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                    {[
                        ['edit/contact', 'Returns "not found" for all valid contacts'],
                        ['activity/create_alert', '"Missing parameter" regardless of payload'],
                        ['activity/create_call', 'Same error — every payload tested'],
                        ['activity/create_message', 'Broken at the API level'],
                    ].map(([ep, desc], i) => (
                        <div key={i} className="flex gap-3 items-center p-3 bg-yellow-500/5 border border-yellow-500/15 rounded-lg">
                            <code className="text-xs font-data text-yellow-400">{ep}</code>
                            <span className="text-xs text-[#F0EFF4]/40">{desc}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Blocked */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="text-xs font-data uppercase tracking-wider text-red-400">7 Endpoints — Permission Denied</span>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                    {[
                        'import/project', 'import/contact', 'import/activity',
                        'import/file', 'import/tags', 'files/create_project_file', 'events/availability'
                    ].map((ep, i) => (
                        <div key={i} className="flex gap-3 items-center p-3 bg-red-500/5 border border-red-500/15 rounded-lg">
                            <code className="text-xs font-data text-red-400">{ep}</code>
                        </div>
                    ))}
                </div>
            </div>
            <p className="text-xs text-[#F0EFF4]/30 text-center italic">
                All 18 endpoints were tested with live data. No guessing. These are real results from a real ProLine partner account.
            </p>
        </div>
    </div>
);

// ─── INFOGRAPHIC 3: Rain Day Decision Engine ──────────────────────────────────
const RainDayEngine = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: How the Rain Day Decision Engine Works
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row gap-4 items-stretch mb-6">
                {[
                    { icon: '📍', label: 'Job Address', detail: 'From ProLine pipeline' },
                    { icon: '🗺️', label: 'Geocoding', detail: 'Free, via OpenStreetMap' },
                    { icon: '🌦️', label: 'Weather Forecast', detail: 'Free, via Open-Meteo' },
                    { icon: '⚖️', label: 'Risk Scoring', detail: 'Configurable rubric' },
                    { icon: '📞', label: 'Decision Output', detail: 'GO / CALL / AUTO-CALL' },
                ].map((step, i) => (
                    <React.Fragment key={i}>
                        <div className="flex-1 flex flex-col items-center text-center p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5">
                            <div className="text-2xl mb-2">{step.icon}</div>
                            <div className="font-heading font-bold text-[#F0EFF4] text-xs mb-1">{step.label}</div>
                            <div className="text-[#F0EFF4]/40 text-xs">{step.detail}</div>
                        </div>
                        {i < 4 && <div className="hidden md:flex items-center text-[#7B61FF]/40 text-lg px-1">›</div>}
                    </React.Fragment>
                ))}
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {[
                    { label: 'GO', color: 'green', condition: 'Precip below 50% or under 5mm. Crews go out. Nothing to do.', icon: '✅' },
                    { label: 'CALL', color: 'yellow', condition: 'Rain 50%+ and 5mm+, or wind over 40km/h. Agent flags for human decision.', icon: '📞' },
                    { label: 'AUTO-CALL', color: 'red', condition: 'Rain 70%+ and 10mm+. Agent notifies and drafts reschedule — no human needed.', icon: '🚨' },
                ].map((d, i) => (
                    <div key={i} className={`p-4 rounded-xl border text-center
                        ${d.color === 'green' ? 'bg-green-500/5 border-green-500/20' :
                          d.color === 'yellow' ? 'bg-yellow-500/5 border-yellow-500/20' :
                          'bg-red-500/5 border-red-500/20'}`}>
                        <div className="text-xl mb-2">{d.icon}</div>
                        <div className={`font-data font-bold text-sm mb-2
                            ${d.color === 'green' ? 'text-green-400' :
                              d.color === 'yellow' ? 'text-yellow-400' : 'text-red-400'}`}>
                            {d.label}
                        </div>
                        <div className="text-xs text-[#F0EFF4]/50 leading-relaxed">{d.condition}</div>
                    </div>
                ))}
            </div>
            <p className="text-xs text-[#F0EFF4]/30 text-center mt-6 italic">
                Runs every morning at 5am and every evening at 9pm. Both free APIs. Zero subscription cost.
            </p>
        </div>
    </div>
);

// ─── INFOGRAPHIC 4: Integration Roadmap ──────────────────────────────────────
const IntegrationRoadmap = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: What Is Coming Next
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="space-y-3">
                {[
                    { phase: 'Phase 1', label: 'ProLine Webhooks via Make.com', priority: 'HIGH', status: 'Designed', detail: 'ProLine sends project, quote, and invoice events to the agent automatically. Removes polling. Gets us financial data we cannot write via API.' },
                    { phase: 'Phase 2', label: 'CallRail Integration', priority: 'HIGH', status: 'Not started', detail: 'Marketing attribution. Cost per lead by source. Which campaigns are producing real revenue, not just calls.' },
                    { phase: 'Phase 3', label: 'Response Time Tracking', priority: 'MEDIUM', status: 'Designed', detail: '"Left on read" detection. When a customer replies and a rep hasn\'t responded in X hours, the agent flags it.' },
                    { phase: 'Phase 4', label: 'Additional ProLine API Access', priority: 'MEDIUM', status: 'Request submitted', detail: 'Financial fields, contact editing, activity logging. Waiting on ProLine partner team to expand permissions.' },
                    { phase: 'Phase 5', label: 'CompanyCam + QuickBooks', priority: 'LOW', status: 'Future', detail: 'Field photos into job records. Invoice matching to QuickBooks. Full job lifecycle in one place.' },
                ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5">
                        <div className="flex-shrink-0 w-16 text-right pt-0.5">
                            <span className="text-xs font-data text-[#7B61FF] uppercase tracking-wider">{item.phase}</span>
                        </div>
                        <div className="w-px bg-[#7B61FF]/20 self-stretch mx-2" />
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-heading font-bold text-[#F0EFF4] text-sm">{item.label}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-data border
                                    ${item.priority === 'HIGH' ? 'text-red-400 bg-red-500/10 border-red-500/20' :
                                      item.priority === 'MEDIUM' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' :
                                      'text-[#F0EFF4]/40 bg-[#F0EFF4]/5 border-[#F0EFF4]/10'}`}>
                                    {item.priority}
                                </span>
                                <span className="text-xs text-[#F0EFF4]/30">{item.status}</span>
                            </div>
                            <div className="text-xs text-[#F0EFF4]/50 leading-relaxed">{item.detail}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ProlineIntegration4Hours = () => {
    return (
        <BlogLayout
            title="I Taught RoofClaw How to Use ProLine in 4 Hours"
            date="Mar 09, 2026"
            readTime={9}
            heroImage="/images/blog/proline-integration-4-hours-hero.png"
            description="What we built, what we learned, and exactly how to do it. A full build log of teaching an AI agent to read, write, and reason inside ProLine CRM — in one session."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                ProLine is used by roofing companies all over North America.
            </p>
            <p>
                Most of them log in manually every morning. Check the pipeline. Update stages. Book appointments. Send follow-ups. Write notes. It is slow, it is repetitive, and it is exactly the kind of work that an AI agent should be doing instead.
            </p>
            <p>
                On March 9, 2026, we spent four hours building the integration. By the end, a live RoofClaw agent could read the pipeline, book appointments, score deals, draft customer emails, and make rain day scheduling decisions completely on its own.
            </p>
            <p>
                Here is exactly what happened, what we built, and what any ProLine user can do with it.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Why We Started Here</h2>
            <p>
                CRM integrations are the nerve center of any roofing business. Every lead, every appointment, every follow-up, every won job flows through the CRM. If an AI agent cannot see and interact with the CRM, it is essentially blind to the most important data in the business.
            </p>
            <p>
                ProLine is a strong product. It is built specifically for the trades, it handles the full sales cycle from lead to invoice, and it has a partner API that gives programmatic access to the data. The API is not perfect. But it is enough.
            </p>
            <p>
                The goal was to build a genuine, production-quality integration. Not a proof of concept. Not a simple read-only connection. A full skill that an agent could actually run a roofing company's operations on.
            </p>
            <p>
                Four hours later, that is what we had.
            </p>

            <FourHourTimeline />

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Hour One: The API Audit</h2>
            <p>
                Every integration starts with the same question: what does the API actually do, versus what the documentation says it does?
            </p>
            <p>
                For ProLine, the gap between the two turned out to be significant. We hit 18 endpoints with real requests against a live account, documented every response, and mapped each one into one of three categories.
            </p>
            <p>
                Seven endpoints worked exactly as expected and became the foundation of the skill. Four endpoints were accepted by the API without error but returned incorrect results or failed silently under all tested conditions. Seven endpoints returned permission-denied responses, regardless of what credentials we sent.
            </p>

            <APIStatusBoard />

            <p>
                The broken endpoints matter because they look like they should work. The <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">edit/contact</code> endpoint accepts valid requests, returns no error, and then returns "contact does not exist" for every valid contact ID in the system. If you built a workflow that relied on it without testing, you would never know it was failing.
            </p>
            <p>
                The activity endpoints are similar. They accept the calls, return errors that say "missing parameter," and yet every parameter their documentation specifies was included. This is not a documentation problem. The endpoints are broken at the API level.
            </p>
            <p>
                Documenting this honestly was as important as documenting what works. An agent that tries broken endpoints will produce silent failures. An agent that knows which endpoints to skip will not.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Hour Two: The Database</h2>
            <p>
                The next problem with any CRM API is rate limits. ProLine needs about five seconds between calls to the same endpoint. If an agent wants to pull a full pipeline report and there are 40 active projects, that is 40 API calls, running at one every five seconds, taking three-plus minutes before the agent can even start answering questions.
            </p>
            <p>
                The solution is a local SQLite database. Once a day, a sync script pulls everything from ProLine and stores it locally. The agent queries the local database for reporting, analysis, and anything that does not require real-time data. It only hits the live API when it needs to write something.
            </p>
            <p>
                We designed the schema during hour two. Thirteen tables covering every object the working API returns: projects, contacts, events, quotes, invoices, deal quality scoring, notes history, email drafts, response tracking, CallRail call data, weather checks, rain day decisions, geocoding cache, sync logs, and webhook event logs.
            </p>
            <p>
                The tables we cannot populate yet (quotes, invoices, CallRail) are designed and waiting. When those integrations come online, the schema is already there.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Hour Three: The Rain Day Engine</h2>
            <p>
                This is the feature that gets roofers' attention when we show it.
            </p>
            <p>
                Roofing companies lose money on rain days. Not just because crews do not work. Because the information chain breaks down. Someone has to check the weather. Someone has to figure out which jobs are affected. Someone has to call homeowners. Someone has to reschedule. When you have ten jobs on the board and a storm coming, that is two hours of phone calls and admin on a morning you can least afford it.
            </p>
            <p>
                The rain day engine automates the entire thing.
            </p>
            <p>
                It starts with the job addresses already in ProLine. It geocodes each one using OpenStreetMap, which is free. It pulls a 48-hour forecast from Open-Meteo, also free. It scores each address against a risk rubric: rain probability, expected precipitation volume, wind speed, and a trend comparison between the previous evening's forecast and the current morning.
            </p>
            <p>
                The output is a decision for each job: GO, CALL, or AUTO-CALL. GO means nothing to do. CALL means the agent flags it for a human to decide. AUTO-CALL means the threshold is high enough that the agent notifies the homeowner and drafts a reschedule without waiting for someone to tell it to.
            </p>

            <RainDayEngine />

            <p>
                The rubric thresholds are configurable per client. A company that works through light rain sets them differently than one that pulls crews at the first forecast. The defaults cover most situations, but every roofing company has different risk tolerance and the engine respects that.
            </p>
            <p>
                Both scripts run on cron. Weather check runs at 9pm the evening before and at 5am the morning of. The evening run builds the trend baseline. The morning run compares against it and flags anything that deteriorated overnight. The whole thing runs in under 30 seconds and uses no paid APIs.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Hour Four: The Knowledge Base</h2>
            <p>
                A working API connection is not enough. An AI agent needs to understand context, constraints, and rules before it can operate safely inside a live system.
            </p>
            <p>
                Hour four was documentation. Every critical gotcha we discovered during testing. The fact that stage changes require stage IDs, not string names, and that using strings silently fails with no error. The fact that financial fields (quoted value, revenue, cost) are read-only and cannot be written via API. The fact that the notes field is a single text blob, not an array, and that writing it overwrites whatever was there before, so the agent always reads the existing notes first and appends rather than replacing.
            </p>
            <p>
                These rules matter. An agent that does not know them will make mistakes that look like they worked. A stage update that used a string name would return success and do nothing. A note write that did not read first would destroy the existing job history. The documentation is what turns a connected agent into a trustworthy one.
            </p>
            <p>
                Seven workflow recipes were documented in full: new lead intake, appointment booking, post-inspection notes with email draft, pipeline reporting, context-aware follow-up, response time monitoring, and marketing attribution. Each one is a step-by-step procedure the agent can execute from scratch, with the exact endpoint calls, field requirements, and gotchas in sequence.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What the Agent Can Do Now</h2>
            <p>
                At the end of the session, the agent could:
            </p>
            <ul className="list-disc pl-6 mb-8 text-[#F0EFF4]/80 space-y-3">
                <li><strong className="text-[#F0EFF4]">Create and update project records</strong> — New leads enter the CRM automatically, with contact records, stage assignment, and source attribution.</li>
                <li><strong className="text-[#F0EFF4]">Book and cancel appointments</strong> — The agent schedules inspections directly in ProLine, assigns them to the right rep, and updates the project stage.</li>
                <li><strong className="text-[#F0EFF4]">Write structured job notes and draft emails</strong> — After an inspection, the agent writes a structured note to the CRM and drafts a customer follow-up email inside the CRM record, ready for the rep to review and send.</li>
                <li><strong className="text-[#F0EFF4]">Run pipeline reports from local data</strong> — Deal quality grades (A through F), overdue follow-ups, pipeline by stage and status — all queryable in seconds from the local database.</li>
                <li><strong className="text-[#F0EFF4]">Make rain day decisions</strong> — Every morning, the agent checks tomorrow's weather against every active job and flags or acts based on the configured rubric.</li>
                <li><strong className="text-[#F0EFF4]">Look up team members</strong> — When assigning jobs, the agent can look up any rep by email and get their system ID for correct assignment.</li>
            </ul>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What It Cannot Do Yet</h2>
            <p>
                Honest accounting. The broken and blocked endpoints mean there are gaps.
            </p>
            <p>
                The agent cannot currently update contact records. The <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">edit/contact</code> endpoint is broken at the API level. New contacts can be created via <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">edit/project</code>, but existing contact updates have to go through the ProLine UI until this is resolved.
            </p>
            <p>
                Activity logging is also blocked. The agent cannot log calls, send messages, or create activity alerts through the API. These are the highest-value missing pieces and they are already on the request list for the ProLine partner team.
            </p>
            <p>
                Financial data (quotes, invoices, revenue) is read-only and only accessible via webhooks, which are designed but not yet connected. Once the Make.com webhook pipeline is live, the local database will have full financial visibility and the pipeline reporting will be complete.
            </p>

            <IntegrationRoadmap />

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Meta-Example, Again</h2>
            <p>
                Something worth noting about how this build session actually ran.
            </p>
            <p>
                The AI agent (Lucy) did not just assist during the session. She ran most of it. She made the API calls, interpreted the responses, identified the broken endpoints, designed the database schema, wrote the sync scripts, built the weather engine, and authored the knowledge base — including this entire quick reference guide. Adam provided the domain expertise, the ProLine partner credentials, and the decisions on what mattered most.
            </p>
            <p>
                The knowledge base that the agent built is what the agent uses to operate. She wrote her own instruction manual in real time and is now running from it. That is not a metaphor. That is literally how it worked.
            </p>
            <p>
                This is what RoofClaw delivers for roofing companies. Not a chatbot that answers questions. A configured, documented, production-quality AI agent that knows your tools, your workflows, and your gotchas — because it helped build them.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden not-prose">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none" />
                <h2 className="text-3xl font-heading font-bold mb-6 text-[#F0EFF4]">What This Means For You</h2>
                <p className="text-[#F0EFF4]/80 mb-6">
                    If your roofing company runs on ProLine, this integration is available. No custom development. No learning curve. No waiting.
                </p>
                <ul className="space-y-3 mb-8">
                    {[
                        'Your agent knows ProLine before it ships.',
                        'Rain day decisions run automatically, every morning.',
                        'Pipeline reports are instant. No manual queries.',
                        'Every job note and email draft lands in your CRM where your team already works.',
                        'The broken and blocked endpoints are documented so the agent never silently fails.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3 text-[#F0EFF4]/80 text-sm">
                            <span className="text-[#7B61FF] flex-shrink-0 mt-0.5">→</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
                <p className="text-xl font-drama italic text-[#7B61FF]">
                    "Four hours to build it. Zero hours for you to maintain it."
                </p>
            </div>

            <p className="mt-12">
                If you are a ProLine user and you want to see this running on your account, book a call. We will walk you through exactly what the agent does, what it cannot do yet, and what the roadmap looks like. No guessing. No black box. Just a live demo on real data.
            </p>

        </BlogLayout>
    );
};

export default ProlineIntegration4Hours;
